import React from "react";
import { Link } from "react-router-dom";
import { VideoCamera, TextAa, FilmSlate, Waveform, Microphone, MusicNotes, Gift, Broadcast, Sparkle, ArrowRight, Megaphone } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const TOOLS = [
  { to: "/studio", icon: TextAa, label: "Text to Video", sub: "Type it, animate it, post it", tone: "from-sky-500/30 to-sky-900/10", testid: "hub-studio" },
  { to: "/ai-video", icon: FilmSlate, label: "AI Text-to-Video", sub: "Script → narrated scenes", tone: "from-fuchsia-500/25 to-fuchsia-900/10", ai: true, testid: "hub-ai-video" },
  { to: "/ai-voice", icon: Waveform, label: "AI Voice", sub: "Text-to-speech, 19 voices", tone: "from-emerald-500/25 to-emerald-900/10", ai: true, testid: "hub-ai-voice" },
  { to: "/ai-voiceover", icon: Megaphone, label: "AI Voiceover", sub: "Narrate a clip you upload", tone: "from-amber-500/25 to-amber-900/10", ai: true, testid: "hub-ai-voiceover" },
  { to: "/ai-song", icon: MusicNotes, label: "AI Song", sub: "Your title, lyrics & vibe", tone: "from-rose-500/25 to-rose-900/10", ai: true, testid: "hub-ai-song" },
  { to: "/voice", icon: Microphone, label: "Voice Studio", sub: "Record with Voice FX", tone: "from-violet-500/25 to-violet-900/10", testid: "hub-voice" },
  { to: "/gifts/create", icon: Gift, label: "Create a Gift", sub: "Design & sell in the Gift Shop", tone: "from-yellow-400/25 to-yellow-900/10", testid: "hub-gift" },
  { to: "/live/go", icon: Broadcast, label: "Go Live", sub: "Stream, gifts & announcer", tone: "from-red-500/25 to-red-900/10", testid: "hub-live" },
];

/** CreateHub — the "+" tab: one place for every way to create on Giffwurldaunce. */
export default function CreateHub() {
  const { user } = useAuth();
  return (
    <div className="min-h-[100dvh] bg-ink pb-40 px-5 pt-8" data-testid="create-hub">
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Create</p>
      <h1 className="text-cream text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] mt-1">
        What are we<br /><span className="text-sky-300">making</span> today{user?.display_name ? `, ${user.display_name.split(" ")[0]}` : ""}?
      </h1>

      <Link to="/create" data-testid="hub-upload" className="group mt-7 block rounded-[28px] p-5 bg-papaya text-ink relative overflow-hidden papaya-glow animate-fade-up">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/20 blur-2xl group-hover:scale-125 transition-transform duration-500" />
        <div className="flex items-center gap-4 relative">
          <div className="w-14 h-14 rounded-2xl bg-ink/15 flex items-center justify-center"><VideoCamera size={30} weight="fill" /></div>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase font-black opacity-70">Most popular</p>
            <p className="text-2xl font-black tracking-tight leading-none mt-0.5">Upload a dance video</p>
            <p className="text-sm opacity-80 mt-1">Vertical clip, music, AI captions, hashtags</p>
          </div>
          <ArrowRight size={22} weight="bold" className="group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {TOOLS.map((t, i) => (
          <Link key={t.to} to={t.to} data-testid={t.testid} style={{ animationDelay: `${80 + i * 50}ms` }} className={`group relative rounded-3xl p-4 border border-white/10 bg-gradient-to-br ${t.tone} hover:border-white/30 hover:-translate-y-0.5 transition-all duration-300 animate-fade-up ${i % 3 === 0 ? "min-h-[150px]" : "min-h-[130px]"}`}>
            {t.ai && <span className="absolute top-3 right-3 inline-flex items-center gap-0.5 text-[9px] font-black tracking-[0.15em] uppercase text-cream/80 bg-ink/40 px-1.5 py-0.5 rounded-full"><Sparkle size={9} weight="fill" /> AI</span>}
            <t.icon size={24} weight="duotone" className="text-cream" />
            <p className="text-cream font-black text-base mt-3 leading-tight">{t.label}</p>
            <p className="text-cream/55 text-[11px] mt-0.5 leading-snug">{t.sub}</p>
          </Link>
        ))}
      </div>
      <p className="text-cream/30 text-[11px] mt-6">Anything made with AI tools is labelled “AI generated” when posted.</p>
    </div>
  );
}
